export type ResponseConfig = {
  MaxItems: number;
  PageSize: number;
  StartingToken: string | null;
};

export type ModelRef = {
  model_id: string;
  model_family: string;
  api_key_secret?: string | null;
};

/** One pipeline step's model and prompt template. Both optional; omitted means
 *  inherited, which is what every pre-existing context amounts to. */
export type StageConfig = {
  model?: ModelRef | null;
  template_id?: string | null;
  template_version?: number | null;
};

export type ContextItem = {
  context_id: string;
  description: string;
  chat:{
    model_id: string;
    model_family: string;
    api_key_secret: string | null;
  };
  embedding:{
    model_id: string;
    model_family: string;
    api_key_secret: string | null;
    embedding_dim: number | undefined | null;
  };
  vision:{
    model_id: string;
    model_family: string;
    api_key_secret: string | null;
  }
  /** Per-step configuration: the model a step runs on and the template its prompts
   *  came from. One map rather than the eight fields it replaces, which described one
   *  thing in eight places.
   *
   *  Everything omitted is inherited: a step with no model uses `chat` (`summarize`
   *  through `extraction` first, `vision` from its own field), and one with no
   *  template uses the shipped prompts. Absent entirely for every context created
   *  before it existed. */
  stages?: Record<string, StageConfig> | null;
  /** The flat fields this replaces. Still read, because a context written against
   *  them is still valid -- nothing was migrated. */
  extraction?: ModelRef | null;
  summarize?: ModelRef | null;
  keywords?: ModelRef | null;
  generation?: ModelRef | null;
  updated_at: string;
  /** Template these prompts were created from. Provenance only -- the prompts
   *  themselves live in the prompt table and are read from there. Null for a
   *  context created without one, which the UI shows as "custom". */
  prompt_template_id?: string | null;
  /** Superseded by `stages`. Read for contexts that recorded them. */
  prompt_templates?: Record<string, string> | null;
  prompt_template_versions?: Record<string, number> | null;
  /** That template's version as of creation. A snapshot, not a subscription:
   *  comparing it with the template's current version is what surfaces
   *  "this template has been updated". */
  prompt_template_version?: number | null;
  // Graph statistics fields
  node_count?: number | null;
  edge_count?: number | null;
  average_degree?: number | null;
  average_clustering_coefficient?: number | null;
  graph_stats_updated_at?: string | null;
  // todo delete these parameter
  api_key_secret: string;
  model_family: string;
  model_id: string;
};

export interface GraphStatsResponse {
  node_count: number | null;
  edge_count: number | null;
  average_degree: number | null;
  average_clustering_coefficient: number | null;
  graph_stats_updated_at: string | null;
}

export type GetContextResponse = {
  contexts: ContextItem[];
};

export const queryEngineOptions = ["gremlin", "sparql", "openCypher"] as const;
export type QueryEngine = (typeof queryEngineOptions)[number];

export const neptuneServiceTypeOptions = [
  "neptune-db",
  "neptune-graph",
] as const;
export type NeptuneServiceType = (typeof neptuneServiceTypeOptions)[number];

export type ConnectionConfig = {
  /**
   * Base URL to access to the database through HTTPs endpoints
   */
  url: string;
  /**
   * Choose between gremlin or sparQL engines.
   * By default, it uses gremlin
   */
  queryEngine?: QueryEngine;
  /**
   * If the service is Neptune,
   * all requests should be sent through the nodejs proxy-server.
   */
  proxyConnection?: boolean;
  /**
   * If it is Neptune, the URL of the database.
   */
  graphDbUrl?: string;
  /**
   * If it is Neptune, it could need authentication.
   */
  awsAuthEnabled?: boolean;
  /**
   * If it is Neptune, it could need authentication.
   */
  serviceType?: NeptuneServiceType;
  /**
   * AWS Region where the Neptune cluster is deployed.
   * It is needed to sign requests.
   */
  awsRegion?: string;
  /**
   * Number of milliseconds before aborting a request.
   * By default, undefined.
   */
  fetchTimeoutMs?: number;
  /**
   * A default limit on the number of nodes that can be expanded in one query.
   * By default, undefined.
   */
  nodeExpansionLimit?: number;
};

/**
 * Represents a connection config with the ID and display label integrated in to
 * the type.
 *
 * This makes it a bit easier to deal with compared to the connection inside the
 * `RawConfiguration` type since that one has a bunch of other properties and
 * the connection is optional.
 */
export type ConnectionWithId = ConnectionConfig & {
  id: string;
  displayLabel?: string;
};

export interface GenContentPagination {
  current_page: number;
  has_more: boolean;
  total_items: number;
  total_pages: number;
}

export interface GenContentPaginationDDB {
  has_more: boolean;
  last_evaluated_key: string | null;
}

export interface VectorChunkSource {
  chunk_order_index: number;
  content: string;
  context_id: string;
  full_doc_id: string;
  id: string;
  tokens: number;
  __vector__: string[];
}


export interface VectorChunkItem {
  id: string;
  score: number;
  source: VectorChunkSource;
}
export interface VectorEntitySource {
  context_id: string;
  entity_name: string;
  id: string;
  __vector__: string[];
}
export interface VectorEntityItem {
  id: string;
  score: number;
  source: VectorEntitySource;
}

export interface VectorRelationSource {
  context_id
  id: string;
  src_id: string;
  tgt_id: string;
  __vector__: string[];
}

export interface VectorRelationItem {
  id: string;
  score: number;
  source: VectorRelationSource;
}


export interface DDBChunkItem {
  chunk_order_index: string;
  content: string;
  context_id: string;
  full_doc_id: string;
  id: string;
  tokens: string;
}

export interface DDBFullDocItem {
  content: string;
  context_id: string;
  id: string;
}

export interface DDBResponseCacheItem {
  original_prompt: string;  
  embedding: number[] | null;
  embedding_shape: number[] | null;
  mode: string;
  return: string;
  context_id: string;
  embedding_min: number | null;
  id: string;
  embedding_max: number | null;
}

export interface VectorChunkResponse {
  items: VectorChunkItem[];
  pagination: GenContentPagination;
}

export interface VectorEntityResponse {
  items: VectorEntityItem[];
  pagination: GenContentPagination;
}

export interface VectorRelationResponse {
  items: VectorRelationItem[];
  pagination: GenContentPagination;
}

export interface DDBChunkResponse {
  items: DDBChunkItem[];
  pagination: GenContentPaginationDDB;
}

export interface DDBFullDocResponse {
  items: DDBFullDocItem[];
  pagination: GenContentPaginationDDB;
}

export interface DDBResponseCacheResponse {
  items: DDBResponseCacheItem[];
  pagination: GenContentPaginationDDB;
}

export interface ModelItem {
  model_family: string;
  model_id: string;
  need_api_key: boolean;
  embedding_dim: number | undefined | null;
}

export interface ApiKeyItem {
  name: string;
}

export interface Tag {
  key: string;
  value: string;
}

export interface TagFilter {
  tags: Tag[];
  operator: "AND" | "OR";
}

export interface McpApiKeyInfo {
  key_id: string;
  name: string;
  created_at: string;
  expires_at: string | null;
  never_expires: boolean;
}

export interface CreateMcpApiKeyResponse {
  api_key: string;
  key_id: string;
  name: string;
  created_at: string;
  expires_at: string | null;
  never_expires: boolean;
}
